import { motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/app/components/ui/Button'
import { ChevronLeft, ChevronRight } from '@/app/components/ui/Icons'
import { cn } from '~/utils/cn'

import Slide1 from '/assets/login/1.png'
import Slide2 from '/assets/login/2.png'
import Slide3 from '/assets/login/3.png'
import Slide4 from '/assets/login/4.png'

type ImageSliderItemProps = {
  title?: string
  subtitle?: string
  src: string
  alt: string
}

type ImageSliderProps = {
  className?: string
}

const SLIDE_DURATION = 5000
const BAR_MAX_WIDTH = 90
const BAR_MIN_WIDTH = 6

export const LoginSlider = ({ className }: ImageSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const { t } = useTranslation()

  const items: Array<ImageSliderItemProps> = [
    {
      src: Slide1,
      title: t('page.login.image_slide.slide_1.title')!,
      subtitle: t('page.login.image_slide.slide_1.subtitle')!,
      alt: t('page.login.image_slide.slide_1.image_alt')!,
    },
    {
      src: Slide2,
      title: t('page.login.image_slide.slide_2.title')!,
      subtitle: t('page.login.image_slide.slide_2.subtitle')!,
      alt: t('page.login.image_slide.slide_2.image_alt')!,
    },
    {
      src: Slide3,
      title: t('page.login.image_slide.slide_3.title')!,
      subtitle: t('page.login.image_slide.slide_3.subtitle')!,
      alt: t('page.login.image_slide.slide_3.image_alt')!,
    },
    {
      src: Slide4,
      title: t('page.login.image_slide.slide_4.title')!,
      subtitle: t('page.login.image_slide.slide_4.subtitle')!,
      alt: t('page.login.image_slide.slide_4.image_alt')!,
    },
  ]

  useEffect(() => {
    // Used to change slides
    const timeout = setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === items.length - 1 ? 0 : prevIndex + 1
      )
    }, SLIDE_DURATION)
    // Used to update slide "loading" bar
    const interval = setInterval(() => {
      setCurrentTime((prevTime) => prevTime + 10)
    }, 10)
    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
      setCurrentTime(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  // Animations Variants
  const slideVariants = {
    loaded: { opacity: 0, y: -25, transition: { duration: 0.7 } },

    loading: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay: 0.7 },
    },

    toLoad: { opacity: 0, y: 25, transition: { duration: 0 } },
  }

  const loadBarFillVariants = {
    loaded: { opacity: 1 },
    loading: { opacity: 1, transition: { duration: 2 } },
    toLoad: { opacity: 0 },
  }

  const loadBarVariants = {
    loaded: {
      width: BAR_MIN_WIDTH,
    },
    loading: {
      width: BAR_MAX_WIDTH,
      transition: { type: 'spring', bounce: 0.2 },
    },
    toLoad: {
      width: BAR_MIN_WIDTH,
      transition: { duration: 0.4 },
    },
  }

  const moveToIndex = (movement: 'prev' | 'next') => {
    if (movement === 'next') {
      setCurrentIndex((current) =>
        current === items.length - 1 ? 0 : current + 1
      )
      return
    }
    setCurrentIndex((current) =>
      current === 0 ? items.length - 1 : current - 1
    )
  }

  return (
    <section
      className={cn(
        className,
        'flex flex-col gap-10 overflow-hidden rounded-md border border-border/4 bg-under/10 pb-6'
      )}
    >
      <div className="relative flex-1">
        {items.map(({ title, subtitle, src, alt }, index) => {
          return (
            <motion.div
              key={index}
              variants={slideVariants}
              initial="toLoad"
              animate={
                index === currentIndex
                  ? 'loading'
                  : index === currentIndex - 1 ||
                    (index === items.length - 1 && currentIndex === 0)
                  ? 'loaded'
                  : 'toLoad'
              }
              className="absolute h-full w-full"
            >
              <div className="relative h-full w-full">
                <img
                  className={cn(
                    'absolute bottom-0 top-0 my-auto h-[45vh] max-w-none xs:h-[50vh] sm:h-[60vh] lg:h-[75vh] xl:h-[80vh] ',
                    index == 0 && 'left-[50%] -translate-x-[22%]',
                    index == 1 && 'right-[50%] translate-x-[29%]',
                    index == 2 && 'right-[50%] translate-x-[29%]',
                    index == 3 && 'left-[50%] -translate-x-[26.7%]'
                  )}
                  src={src}
                  alt={alt}
                />

                <div className="absolute -bottom-7 flex w-full flex-col gap-1 xs:bottom-0">
                  <h3 className="text-center text-md font-medium text-highlight">
                    {title}
                  </h3>
                  <h4 className="text-center text-md  text-highlight/48">
                    {subtitle}
                  </h4>
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      <div className="mx-auto flex w-fit items-center gap-1.5 text-highlight">
        <Button
          type="button"
          variant="ghost"
          icon={ChevronLeft}
          size="md"
          className="relative mr-2 lg:mr-5"
          onClick={() => moveToIndex('prev')}
        />
        {items.map((_, index) => {
          return (
            <div key={index} className="flex items-center">
              <motion.div
                className="relative h-1.5 cursor-default rounded-full bg-highlight/32"
                initial="toLoad"
                variants={loadBarVariants}
                animate={
                  index === currentIndex
                    ? 'loading'
                    : index < currentIndex
                    ? 'loaded'
                    : 'toLoad'
                }
              >
                <motion.div
                  className="absolute top-0 h-1.5 w-0 rounded-full bg-highlight"
                  initial="toLoad"
                  variants={loadBarFillVariants}
                  animate={
                    index === currentIndex
                      ? 'loading'
                      : index < currentIndex
                      ? 'loaded'
                      : 'toLoad'
                  }
                  style={{
                    width:
                      index === currentIndex
                        ? (currentTime * (BAR_MAX_WIDTH - BAR_MIN_WIDTH - 1)) /
                            SLIDE_DURATION +
                          BAR_MIN_WIDTH
                        : index < currentIndex
                        ? BAR_MIN_WIDTH
                        : BAR_MIN_WIDTH,
                  }}
                />
              </motion.div>
            </div>
          )
        })}
        <Button
          type="button"
          variant="ghost"
          icon={ChevronRight}
          size="md"
          className="relative ml-2 lg:ml-5"
          onClick={() => moveToIndex('next')}
        />
      </div>
    </section>
  )
}

export default LoginSlider
